import axios from "axios"
import BigNumber from "bignumber.js"

/**
 * @typedef {Object} Wallet
 * @property {number} id - Уникальный идентификатор кошелька
 * @property {string} type - Тип кошелька (например, "merchant", "partner")
 * @property {number} balance - Баланс кошелька
 * @property {string} currency - Валюта кошелька (например, "USD", "EUR")
 */

const state = {
    loaded: false,
    totalBalance: 0,
    partner: {
        balance: 0,
        confirmed: true,
        ref_code: null,
    },
    merchant: {
        balance: 0,
        confirmed: false,
    },
    bill: {
        balance: 0,
        confirmed: false,
    },
    vcard: {
        balance: 0,
        confirmed: false,
    },
    /** @type {Wallet[]} */
    wallets: [],
    transactions: [],
    loading: false,
}

const getters = {
    StateTotalBalance:  state => state.totalBalance,
    StatePartnerBalance: state => {
        if(state.wallets && Array.isArray(state.wallets)) {
            const partnerWallet = state.wallets.find(wallet => wallet.ext_type === 'partner')
            return partnerWallet ? partnerWallet.balance : state.partner.balance
        }
        return state.partner.balance
    },
    StatePartnerConfirmed:  state => state.partner.confirmed,
    StatePartnerRefCode:    state => state.partner.ref_code,
    StateMerchantBalance:   state => {
        if(state.wallets && Array.isArray(state.wallets)) {
            const merchantWallet = state.wallets.find(wallet => wallet.ext_type === 'merchant')
            return merchantWallet ? merchantWallet.balance : state.merchant.balance
        }
        return state.merchant.balance
    },
    StateMerchantConfirmed: state => state.merchant.confirmed,
    StateBillBalance:   state => {
        if(state.wallets && Array.isArray(state.wallets)) {
            const billWallet = state.wallets.find(wallet => wallet.ext_type === 'bill')
            return billWallet ? billWallet.balance : state.bill.balance
        }
        return state.bill.balance
    },
    StateBillConfirmed: state => state.bill.confirmed,
    StateVcardBalance:  state => {
        if(state.wallets && Array.isArray(state.wallets)) {
            const vcardWallet = state.wallets.find(wallet => wallet.ext_type === 'vcard')
            return vcardWallet ? vcardWallet.balance : state.vcard.balance
        }
        return state.vcard.balance
    },
    StateVcardConfirmed:state => state.vcard.confirmed,
    StateAllProducts:   state => state,
    StateWallets:       state => state.wallets,
    StateTransactions:  state => state.transactions,
    StateWalletByType:  state => type => {
        return state.wallets.find(wallet => wallet.ext_type === type)
    },
    StateLoadingWallets: state => state.loading,
}

const actions = {
    // пингуем статус и баланс партнера
    async ActionGetProducts({ commit }) {
        let prods = await axios.get("/api/user/wallet/products/").then(res => {
            return res.data
        })
        if (prods.result && prods.data) {
            try {
                await commit("setProducts", prods.data)
            } catch { }
            return { result: true, data: prods.data }
        } else {
            return { result: false, errors: prods.errors }
        }
    },
    // создаем партнера
    async ActionBecomePartner({ commit }) {
        let status = await axios.get("/api/user/partner/be/").then(res => {
            return res.data
        })
        if (status.result && status.data) {
            try {
                await commit("setPartner", status.data)
            } catch { }
            return { result: true, data: status.data }
        } else {
            return { result: false, errors: status.errors }
        }
    },
    // получаем все кошельки
    async ActionGetAllWallets({ commit }) {
        this.state.wallet.loading = true
        let wallets = await axios.get("/api/user/wallet/all/").then(res => res.data)
        if (wallets.result && wallets.data) {
            try {
                commit("setWallets", wallets.data)
                commit("setTotalBalance", wallets.data.wallets || [])
            } catch (error) {
                console.error("Error committing mutations:", error)
            }
            this.state.wallet.loading = false
            return { result: true, data: wallets.data }
        } else {
            this.state.wallet.loading = false
            return { result: false, errors: wallets.errors }
        }
    },
    // получить кошелек по типу
    async ActionGetWalletByType({ commit }, type) {
        if (!type) return { result: false, errors: ["No type provided"] }
        let wallet = await axios.get(`/api/user/wallet/get_by_type/${type}`).then(res => res.data)
        if (wallet.result && wallet.data) {
            console.log("Wallet data received:", wallet.data)
            try {
                commit("setWalletByType", wallet.data)
            } catch (error) {
                console.error("Error committing mutations:", error)
            }
            return { result: true, data: wallet.data }
        } else {
            return { result: false, errors: wallet.errors }
        }
    },
    // обмен балансами между кошельками
    async ActionExchangeBalances({ commit }, data) {
        if(!data.from_wallet_id) return { result: false, errors: ["No from_wallet_id"] }
        if(!data.to_wallet_id) return { result: false, errors: ["No to_wallet_id"] }
        if(!data.amount) return { result: false, errors: ["No amount"] }
        if(!data.from_currency_code) return { result: false, errors: ["No from_currency_code"] }
        if(!data.to_currency_code) return { result: false, errors: ["No to_currency_code"] }

        let response = await axios.post("/api/user/wallet/exchange/", {
            from_wallet_id:     data.from_wallet_id,
            to_wallet_id:       data.to_wallet_id,
            amount:             data.amount,
            from_currency_code: data.from_currency_code,
            to_currency_code:   data.to_currency_code
        }).then(res => {
            return res.data
        })
        if (response.result) {
            try {
                commit("setWallets", response.data)
                commit("setTotalBalance", response.data.wallets || [])
            } catch { }
            return { result: true, data: response.data }
        } else {
            return { result: false, errors: response.errors }
        }
    },
    async ActionWithdrawWalletToRecipient({ commit }, data) {
        if(!data.wallet_id) return { result: false, errors: ["No wallet_id"] }
        if(!data.amount) return { result: false, errors: ["No amount"] }
        if(!data.currency_code) return { result: false, errors: ["No currency_code"] }
        let response = await axios.post("/api/user/wallet/withdraw/", {
            wallet_id: data.wallet_id,
            recipient_id: (data.recipient_id) ? data.recipient_id : null,
            amount: data.amount,
            currency_code: data.currency_code,
            fields: data.fields || {}
        }).then(res => {
            return res.data
        })
        if (response.result) {
            try {
                // commit("updateWalletBalances", response.data)
            } catch { }
            return { result: true, data: response.data }
        } else {
            return { result: false, errors: response.errors }
        }
    }
}

const mutations = {
    setProducts (state, products) {
        state.loaded = true
        state.totalBalance = products.total_balance
        
        state.partner.balance = products.partner.balance
        state.partner.confirmed = products.partner.confirmed
        state.partner.ref_code = products.partner.ref_code

        state.merchant.balance = products.merchant.balance
        state.merchant.confirmed = products.merchant.confirmed
        
        state.bill.balance = products.bill.balance
        state.bill.confirmed = products.bill.confirmed

        state.vcard.balance = products.vcard.balance
        state.vcard.confirmed = products.vcard.confirmed
    },
    setPartner (state, partner) {
        state.partner.balance = partner.balance
        state.partner.confirmed = partner.confirmed
        state.partner.ref_code = partner.ref_code
    },
    setWallets (state, data) {
        state.wallets =         (data.wallets) ? data.wallets : []
        state.transactions =    (data.transactions) ? data.transactions : []
    },
    setWalletByType(state, wallet) {
        const type = (wallet && wallet.type) ? wallet.type : null
        if (!type) return
        let existingWallet = state.wallets.find(w => w.type === type)
        if(!existingWallet) return 
        existingWallet = wallet 
        state.wallets = state.wallets.map(w => {
            if (w.type === type) return existingWallet
            return w
        })
        this.commit("updateTotalBalance")
    },
    setTotalBalance(state, wallets) {
        if (!wallets || !Array.isArray(wallets)) {
            console.error("Invalid wallets data provided to setTotalBalance")
            return
        }
        state.totalBalance = 0
        let balance = new BigNumber(0)
        wallets.forEach(wallet => {
            const b = new BigNumber(wallet.balance)
            balance = balance.plus(b)
        })
        state.totalBalance = balance.toFixed(9)
    },
    updateWalletBalances(state, updatedWallets) {
        state.wallets = state.wallets.map(w => {
            const updated = updatedWallets.find(u => u.type === w.type)
            if (updated) {
                const b = new BigNumber(updated.balance)
                w.balance = b.toFixed(9)
            }
            return w
        })
        this.commit("updateTotalBalance")
    },
    updateTotalBalance(state) {
        if (!state.wallets || !Array.isArray(state.wallets)) {
            console.error("Invalid wallets data provided to updateTotalBalance")
            return
        }
        let balance = new BigNumber(0)
        state.wallets.forEach(wallet => {
            const b = new BigNumber(wallet.balance)
            balance = balance.plus(b)
        })
        state.totalBalance = balance.toFixed(9)
    },
    resetWalletState(state) {
        state.loaded = false
        state.totalBalance = 0
        state.partner = {
            balance: 0,
            confirmed: true,
            ref_code: null,
        }
        state.merchant = {
            balance: 0,
            confirmed: false,
        }
        state.bill = {
            balance: 0,
            confirmed: false,
        }
        state.vcard = {
            balance: 0,
            confirmed: false,
        }
        state.wallets = []
        state.transactions = []
        state.loading = false
    }
}

export default {
    state,
    getters,
    actions,
    mutations
}